import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  EditableCell,
  Collection,
  Badge,
  DateRangePicker,
  Picker,
  PickerItem,
  SearchField,
  Switch,
  TextArea,
  ActionButton,
  ActionMenu,
  MenuItem,
  Button,
  DialogTrigger,
  Link,
  Popover,
  Text,
  ToastQueue,
} from "@react-spectrum/s2";
import { today, getLocalTimeZone } from "@internationalized/date";
import { useDateFormatter } from "react-aria";
import { Link as AriaLink } from "react-aria-components";
import ChevronDownIcon from "@react-spectrum/s2/icons/ChevronDown";
import CopyIcon from "@react-spectrum/s2/icons/Copy";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import DuplicateIcon from "@react-spectrum/s2/icons/Duplicate";
import EditIcon from "@react-spectrum/s2/icons/Edit";
import RefreshIcon from "@react-spectrum/s2/icons/Refresh";
import StarFilledIcon from "@react-spectrum/s2/icons/StarFilled";
import ExpressIcon from "icon:@app/assets/AdobeExpressIcon.svg";

import {
  adminKeys,
  useAdminVideos,
  useUpdateAdminVideoComment,
  useAdminJobCurl,
  useAdminJobRetry,
} from "@app/hooks/admin";
import {
  getNewVideoPath,
  useDownloadAllAssets,
  useDuplicateVideo,
  useVideo,
  useVideoAssets,
  useVideoTemplate,
  useVideoTemplates,
} from "@app/hooks/videos";
import { presignURLQueryOptions } from "@app/hooks/s3";
import { useDebounce } from "@app/hooks/useDebounce";
import { getAdminVideos } from "@app/services/admin";
import { downloadProjectsListExcel } from "@app/services/adminReport";
import { getRelativeTime } from "@app/utils";
import {
  getStepName,
  PRE_FLOW_STEPS,
  resolveSteps
} from "../projects/wizardSteps";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const ALL_STATUSES = "all";
const ALL_STEPS = "all";
const ALL_TEMPLATES = "all";
const NO_VALUE = "__none__";

const STATUS_OPTIONS = [
  { id: ALL_STATUSES, label: "All statuses" },
  { id: "completed", label: "Completed" },
  { id: "processing", label: "Processing" },
  { id: "draft", label: "Draft" },
  { id: "failed", label: "Failed" },
];

const STATUS_BADGE = {
  completed: { variant: "positive", label: "Completed" },
  processing: { variant: "informative", label: "Processing" },
  draft: { variant: "yellow", label: "Draft" },
  failed: { variant: "negative", label: "Failed" },
};

// Clickable badge: no link chrome, just a pointer and a keyboard focus ring.
const copyBadgeLinkStyle = style({
  display: "inline-flex",
  cursor: "pointer",
  borderRadius: "default",
  outlineStyle: { default: "none", isFocusVisible: "solid" },
  outlineColor: "focus-ring",
  outlineWidth: 2,
  outlineOffset: 2,
  opacity: { default: 1, isHovered: 0.8 },
});

const JOB_STATUS_BADGE = {
  completed: { variant: "positive", label: "Completed" },
  succeeded: { variant: "positive", label: "Succeeded" },
  generated: { variant: "informative", label: "Generated" },
  running: { variant: "informative", label: "Running" },
  pending: { variant: "yellow", label: "Pending" },
  failed: { variant: "negative", label: "Failed" },
};

const columns = [
  {
    name: "Actions",
    id: "actions",
    width: 80,
  },
  {
    name: "Status",
    id: "status",
    allowsSorting: true,
    width: 130,
  },
  {
    name: "Template",
    id: "templateId",
    allowsSorting: true,
    width: 140,
  },
  {
    name: "Current Step",
    id: "currentStep",
    allowsSorting: true,
    width: 160,
  },
  {
    name: "Project",
    id: "name",
    isRowHeader: true,
    allowsSorting: true,
    minWidth: 200,
  },
  {
    name: "Jobs",
    id: "jobs",
    width: 130,
  },

  {
    name: "Created Date",
    id: "createdAt",
    allowsSorting: true,
    width: 180,
  },
  {
    name: "Last Action",
    id: "updatedAt",
    allowsSorting: true,
    width: 140,
  },
  {
    name: "Customer / Account",
    id: "customerName",
    allowsSorting: true,
    minWidth: 160,
  },
  {
    name: "Created By",
    id: "createdBy",
    allowsSorting: true,
    minWidth: 180,
  },
  {
    name: "Shared With",
    id: "sharedWith",
    allowsSorting: true,
    minWidth: 200,
  },
  {
    name: "Rate",
    id: "rating",
    allowsSorting: true,
    width: 100,
  },
  {
    name: "Feedback Message",
    id: "feedbackMessage",
    allowsSorting: true,
    minWidth: 220,
  },
  {
    name: "Comment",
    id: "comment",
    minWidth: 260,
  },
];

const getCustomerOrAccountLabel = (video) => {
  const parts = [
    video?.customerName,
    video?.accountName && video?.accountName !== video?.customerName
      ? video.accountName
      : null,
    video?.drNumber ? `DR: ${video.drNumber}` : null,
  ].filter(Boolean);

  return parts.join(" • ");
};

const getSharedWith = (video) =>
  Array.isArray(video?.sharedWith) ? video.sharedWith.filter(Boolean) : [];

const getProjectHref = (video) => {
  if (!video?.id) return "/projects";
  // Completed One Adobe projects end in the wizard's handoff step, not a video page.
  if (video.templateId === "one-adobe" && video.status === "completed") {
    return `${getNewVideoPath(video.id)}`;
  }
  if (video.videoPath || video.status === "completed") {
    return `/projects/${video.id}`;
  }
  return getNewVideoPath(video.id);
};

const getProjectStepLabel = (video, templatesById) => {
  const stepId =
    typeof video?.currentStep === "string" ? video.currentStep.trim() : "";
  if (!stepId) return "";

  const templateData = video?.templateId
    ? templatesById?.get(video.templateId)
    : undefined;

  return (
    getStepName(video?.templateId, stepId, templateData) ??
    getStepName("one-adobe", stepId) ??
    getStepName("partnership-value", stepId) ??
    stepId
  );
};

// Position of the current step in the wizard's numbered stepper (same
// numbering as VideoWizard: pre-flow and viewless steps are not counted).
const getProjectStepProgress = (video, templatesById) => {
  const stepId =
    typeof video?.currentStep === "string" ? video.currentStep.trim() : "";
  if (!stepId || !video?.templateId) return null;

  const stepperSteps = resolveSteps(
    video.templateId,
    templatesById?.get(video.templateId),
  ).filter((step) => !PRE_FLOW_STEPS.includes(step.id) && !step.viewless);

  const index = stepperSteps.findIndex((step) => step.id === stepId);
  return index === -1 ? null : { step: index + 1, total: stepperSteps.length };
};

const getDefaultDateRange = () => {
  const end = today(getLocalTimeZone());
  const start = end.subtract({ days: 30 });
  return { start, end };
};

const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

// Older jobs predate generationTimeMs; fall back to their timestamps.
const getJobDurationLabel = (job) => {
  const fallbackMs =
    job.createdAt && job.completedAt
      ? new Date(job.completedAt) - new Date(job.createdAt)
      : null;
  const duration = formatDuration(job.generationTimeMs ?? fallbackMs);
  if (!duration) return null;
  const render = formatDuration(job.renderTimeMs);
  return render && job.generationTimeMs != null
    ? `Took ${duration} (render ${render})`
    : `Took ${duration}`;
};

// Newest first; includes the current video if it predates history tracking.
const getVideoHistory = (video) => {
  const jobs = Array.isArray(video?.jobs) ? video.jobs : [];
  const entries = Array.isArray(video?.videoHistory)
    ? video.videoHistory.filter((entry) => entry?.videoPath)
    : [];
  if (
    video?.videoPath &&
    !entries.some((entry) => entry.videoPath === video.videoPath)
  ) {
    entries.push({
      videoPath: video.videoPath,
      jobId: video.jobId ?? video.modularRenderJobId ?? null,
    });
  }

  // Legacy entries have no timestamps; borrow them from the job that rendered the file.
  const enriched = entries.map((entry) => {
    if (entry.completedAt) return entry;
    const job =
      jobs.find((j) => j.videoPath && j.videoPath === entry.videoPath) ??
      (entry.jobId ? jobs.find((j) => j.jobId === entry.jobId) : undefined);
    if (!job) return entry;
    return {
      ...entry,
      jobId: entry.jobId ?? job.jobId,
      createdBy: entry.createdBy ?? job.createdBy,
      completedAt: job.completedAt,
      generationTimeMs:
        entry.generationTimeMs ??
        job.generationTimeMs ??
        (job.createdAt && job.completedAt
          ? new Date(job.completedAt) - new Date(job.createdAt)
          : undefined),
    };
  });

  const time = (entry) =>
    new Date(entry.completedAt ?? entry.archivedAt ?? 0).getTime() || 0;
  return enriched.sort(
    (a, b) =>
      (b.videoPath === video.videoPath) - (a.videoPath === video.videoPath) ||
      time(b) - time(a),
  );
};

// Mirrors the backend checks in POST /admin/jobs/:jobId/{retry-upload,rerender}.
const UPLOAD_IN_PROGRESS_MS = 10 * 60 * 1000;

const isPresignedUrlExpired = (rawUrl) => {
  try {
    const params = new URL(rawUrl).searchParams;
    const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(
      params.get("X-Amz-Date") ?? "",
    );
    const expiresIn = Number(params.get("X-Amz-Expires"));
    if (!match || !Number.isFinite(expiresIn)) return false;
    const [, y, mo, d, h, mi, s] = match.map(Number);
    return Date.UTC(y, mo - 1, d, h, mi, s) + expiresIn * 1000 <= Date.now();
  } catch {
    return false;
  }
};

const isRetryableRender = (job) =>
  job.type === "mogrt_render" && job.modular !== true && !!job.videoId;

const canRetryUpload = (job) => {
  if (!isRetryableRender(job) || !job.outputUrl) return false;
  if (isPresignedUrlExpired(job.outputUrl)) return false;
  if (job.status === "failed") return true;
  return (
    job.status === "generated" &&
    Date.now() - new Date(job.updatedAt).getTime() > UPLOAD_IN_PROGRESS_MS
  );
};

const canRerender = (job) =>
  isRetryableRender(job) && job.status === "failed" && job.hasRawRenderRequest;

const getJobErrorMessage = (job) =>
  (Array.isArray(job.errors) ? job.errors : [])
    .map((error) => error?.message ?? error?.error_code)
    .filter(Boolean)
    .join("; ");

const getExpressUrl = (video) => {
  const id =
    video?.templateType === "express"
      ? video?.expressId
      : video?.expressDocumentId;
  return id ? `https://new.express.adobe.com/id/${id}?category=none` : null;
};

// Mounted on demand so the per-video asset queries only run for the chosen row.
const AssetsDownloader = ({ videoId, onDone }) => {
  const video = useVideo(videoId);
  const template = useVideoTemplate(video.data?.templateId);
  const { isLoading: isAssetsLoading } = useVideoAssets(videoId);
  const downloadAllAssets = useDownloadAllAssets(videoId);
  const startedRef = useRef(false);

  useEffect(() => {
    if (video.isError) {
      ToastQueue.negative("Failed to load project assets", { timeout: 5000 });
      onDone();
      return;
    }
    if (
      startedRef.current ||
      !video.isSuccess ||
      template.isLoading ||
      isAssetsLoading
    ) {
      return;
    }
    startedRef.current = true;
    downloadAllAssets.mutate(undefined, {
      onError: () =>
        ToastQueue.negative("Failed to download assets", { timeout: 5000 }),
      onSettled: onDone,
    });
  }, [
    video.isError,
    video.isSuccess,
    template.isLoading,
    isAssetsLoading,
    downloadAllAssets,
    onDone,
  ]);

  return null;
};

export const AdminProjects = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "Projects" }]);

  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [stepFilter, setStepFilter] = useState(ALL_STEPS);
  const [templateFilter, setTemplateFilter] = useState(ALL_TEMPLATES);
  const [range, setRange] = useState(getDefaultDateRange);
  const [isAllTime, setIsAllTime] = useState(false);
  const [excludeDevTeam, setExcludeDevTeam] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const updateDebouncedSearch = useDebounce(setDebouncedSearch, 300);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sortDescriptor, setSortDescriptor] = useState({
    column: "createdAt",
    direction: "descending",
  });
  // Template/step options cover the whole date range + status, so they come
  // from the server (page 1) rather than from the rows loaded so far.
  const [filterOptions, setFilterOptions] = useState([]);

  const { data: templates = [] } = useVideoTemplates();
  const templatesById = useMemo(
    () =>
      new Map(
        templates
          .filter((template) => !!template?.id)
          .map((template) => [template.id, template]),
      ),
    [templates],
  );

  const stepPairsByLabel = useMemo(() => {
    const byLabel = new Map();
    for (const option of filterOptions) {
      const pair = {
        templateId: option.templateId ?? null,
        currentStep: option.currentStep ?? null,
      };
      const label = getProjectStepLabel(pair, templatesById) || NO_VALUE;
      const pairs = byLabel.get(label) ?? new Map();
      pairs.set(JSON.stringify(pair), pair);
      byLabel.set(label, pairs);
    }
    return byLabel;
  }, [filterOptions, templatesById]);

  const queryFilters = useMemo(() => {
    const filters = {
      sort: sortDescriptor?.column ?? "createdAt",
      direction: sortDescriptor?.direction ?? "descending",
    };
    if (statusFilter !== ALL_STATUSES) {
      filters.status = statusFilter;
    }
    if (excludeDevTeam) {
      filters.excludeDevTeam = "true";
    }
    if (!isAllTime && range?.start && range?.end) {
      filters.fromDate = range.start.toString();
      filters.toDate = range.end.toString();
    }
    if (templateFilter !== ALL_TEMPLATES) {
      filters.templates = templateFilter;
    }
    const stepPairs = stepPairsByLabel.get(stepFilter);
    if (stepFilter !== ALL_STEPS && stepPairs?.size) {
      filters.steps = JSON.stringify([...stepPairs.values()]);
    }
    const trimmedSearch = debouncedSearch.trim();
    if (trimmedSearch) {
      filters.search = trimmedSearch;
    }
    return filters;
  }, [
    statusFilter,
    isAllTime,
    range,
    templateFilter,
    stepFilter,
    stepPairsByLabel,
    debouncedSearch,
    sortDescriptor,
    excludeDevTeam,
  ]);

  const {
    data,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useAdminVideos(queryFilters);

  const videos = useMemo(
    () => data?.pages?.flatMap((page) => page?.videos ?? []) ?? [],
    [data],
  );
  const totalCount =
    data?.pages?.at(-1)?.pagination?.totalCount ?? videos.length;

  const latestFilterOptions = data?.pages?.[0]?.filterOptions;
  useEffect(() => {
    if (Array.isArray(latestFilterOptions)) {
      setFilterOptions(latestFilterOptions);
    }
  }, [latestFilterOptions]);

  const videosById = useMemo(
    () => new Map(videos.map((video) => [video.id, video])),
    [videos],
  );

  const updateCommentMutation = useUpdateAdminVideoComment();
  const jobCurlMutation = useAdminJobCurl();
  const jobRetry = useAdminJobRetry();

  const handleJobRetry = (jobId, action) => {
    jobRetry.mutate(
      { jobId, action },
      {
        onSuccess: (data) => {
          ToastQueue.positive(
            action === "rerender"
              ? `Re-render started (job ${data?.jobId ?? ""})`
              : "Upload retry started",
            { timeout: 4000 },
          );
        },
        onError: (err) => {
          ToastQueue.negative(err?.message || "Retry failed", {
            timeout: 6000,
          });
        },
      },
    );
  };
  const duplicateVideo = useDuplicateVideo();
  const queryClient = useQueryClient();
  const [downloadingAssetsVideoId, setDownloadingAssetsVideoId] =
    useState(null);
  const clearDownloadingAssets = useCallback(
    () => setDownloadingAssetsVideoId(null),
    [],
  );

  const handleDownloadVideo = async (item, videoPath = item.videoPath) => {
    try {
      const { presignURL } = await queryClient.fetchQuery(
        presignURLQueryOptions(videoPath),
      );
      const link = document.createElement("a");
      link.href = presignURL;
      link.download = `${item.name || item.id}.mp4`;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      ToastQueue.negative("Failed to download video", { timeout: 5000 });
    }
  };

  const handleDuplicate = (item) => {
    duplicateVideo.mutate(
      { id: item.id },
      {
        onSuccess: () => {
          ToastQueue.positive(
            `Duplicated "${item.name || item.id}" into your projects`,
            { timeout: 3000 },
          );
          queryClient.invalidateQueries({
            queryKey: [...adminKeys.all, "videos"],
          });
        },
      },
    );
  };

  const getTemplateKey = (item) =>
    item.templateId || item.templateType || NO_VALUE;

  const templateOptions = useMemo(() => {
    const keys = new Set(filterOptions.map(getTemplateKey));
    return [
      { id: ALL_TEMPLATES, label: "All templates" },
      ...[...keys]
        .map((key) => ({
          id: key,
          label:
            key === NO_VALUE
              ? "No template"
              : (templatesById.get(key)?.name ?? key),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [filterOptions, templatesById]);

  const stepOptions = useMemo(
    () => [
      { id: ALL_STEPS, label: "All steps" },
      ...[...stepPairsByLabel.keys()]
        .map((key) => ({
          id: key,
          label: key === NO_VALUE ? "No step" : key,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [stepPairsByLabel],
  );

  // Drop a template/step selection that no longer exists for the date range.
  useEffect(() => {
    if (
      templateFilter !== ALL_TEMPLATES &&
      filterOptions.length > 0 &&
      !templateOptions.some((option) => option.id === templateFilter)
    ) {
      setTemplateFilter(ALL_TEMPLATES);
    }
    if (
      stepFilter !== ALL_STEPS &&
      filterOptions.length > 0 &&
      !stepPairsByLabel.has(stepFilter)
    ) {
      setStepFilter(ALL_STEPS);
    }
  }, [
    filterOptions,
    templateOptions,
    stepPairsByLabel,
    templateFilter,
    stepFilter,
  ]);

  const [curlByJobId, setCurlByJobId] = useState({});
  const [pendingCurlJobId, setPendingCurlJobId] = useState(null);

  const handleCopyJobId = async (jobId) => {
    try {
      await navigator.clipboard.writeText(jobId);
      ToastQueue.positive("Job ID copied", { timeout: 3000 });
    } catch {
      ToastQueue.negative("Could not copy job ID", { timeout: 5000 });
    }
  };

  const handleProduceCurl = async (jobId) => {
    if (!jobId) return;
    setPendingCurlJobId(jobId);
    try {
      const curl = await jobCurlMutation.mutateAsync({ jobId });
      setCurlByJobId((prev) => ({ ...prev, [jobId]: curl }));
      try {
        await navigator.clipboard.writeText(curl);
        ToastQueue.positive("cURL copied", { timeout: 3000 });
      } catch {
        ToastQueue.neutral("cURL generated", { timeout: 3000 });
      }
    } catch (err) {
      ToastQueue.negative(err?.message || "Failed to generate cURL", {
        timeout: 5000,
      });
    } finally {
      setPendingCurlJobId((current) => (current === jobId ? null : current));
    }
  };

  const formatter = useDateFormatter({
    dateStyle: "medium",
    timeStyle: "short",
  });

  const isSavingComment = (item) =>
    updateCommentMutation.isPending &&
    updateCommentMutation.variables?.id === item.id;

  const submitComment = (item) => (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const nextComment = String(formData.get("comment") ?? "").trim();
    updateCommentMutation.mutate({
      id: item.id,
      comment: nextComment,
    });
  };

  const rows = videos;

  const loadingState = isError
    ? "error"
    : isLoading
      ? "loading"
      : isFetchingNextPage
        ? "loadingMore"
        : "idle";

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage({ cancelRefetch: false });
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const datePart =
        !isAllTime && range?.start && range?.end
          ? `${range.start.toString()}-${range.end.toString()}`
          : "all-time";
      const fileName = `projects-${statusFilter}-${datePart}.xlsx`;
      const allRows = await getAdminVideos(queryFilters);
      const exportRows = allRows.map((item) => ({
        ...item,
        currentStepLabel: getProjectStepLabel(item, templatesById),
      }));
      await downloadProjectsListExcel(exportRows, fileName);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 24,
        size: "full",
        maxHeight: "calc(100dvh - 56px - 48px)",
      })}
    >
      <title>Projects | BeStory</title>
      {downloadingAssetsVideoId && (
        <AssetsDownloader
          key={downloadingAssetsVideoId}
          videoId={downloadingAssetsVideoId}
          onDone={clearDownloadingAssets}
        />
      )}
      <div
        className={style({
          display: "flex",
          alignItems: "center",
          gap: 8,
        })}
      >
        <div className={style({ font: "heading" })}>Projects</div>
        <ActionButton
          isQuiet
          aria-label="Refresh projects"
          isPending={isFetching && !isLoading}
          isDisabled={isLoading}
          onPress={() => refetch()}
        >
          <RefreshIcon />
        </ActionButton>
        <ActionButton
          isQuiet
          aria-label="Downlaod projects"
          isPending={isDownloading}
          isDisabled={totalCount === 0 || isDownloading || isLoading}
          onPress={handleDownloadExcel}
        >
          <DownloadIcon />
        </ActionButton>
      </div>

      <div
        className={style({
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        })}
      >
        <Picker
          aria-label="Filter by status"
          items={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(key) => setStatusFilter(String(key ?? ALL_STATUSES))}
          styles={style({ width: 200 })}
        >
          {(item) => (
            <PickerItem id={item.id} textValue={item.label}>
              {item.label}
            </PickerItem>
          )}
        </Picker>
        <Picker
          aria-label="Filter by template"
          items={templateOptions}
          value={templateFilter}
          onChange={(key) => setTemplateFilter(String(key ?? ALL_TEMPLATES))}
          styles={style({ width: 200 })}
        >
          {(item) => (
            <PickerItem id={item.id} textValue={item.label}>
              {item.label}
            </PickerItem>
          )}
        </Picker>
        <Picker
          aria-label="Filter by current step"
          items={stepOptions}
          value={stepFilter}
          onChange={(key) => setStepFilter(String(key ?? ALL_STEPS))}
          styles={style({ width: 200 })}
        >
          {(item) => (
            <PickerItem id={item.id} textValue={item.label}>
              {item.label}
            </PickerItem>
          )}
        </Picker>
      </div>
      <div
        className={style({
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        })}
      >
        <SearchField
          aria-label="Search projects"
          placeholder="Search project, user, customer, feedback, comment"
          value={search}
          onChange={(value) => {
            setSearch(value);
            updateDebouncedSearch(value);
          }}
          onClear={() => {
            setSearch("");
            updateDebouncedSearch.cancel();
            setDebouncedSearch("");
          }}
          styles={style({ width: 320 })}
        />
        <DateRangePicker
          aria-label="Filter by created date"
          value={range}
          onChange={(newRange) => {
            if (newRange) {
              setRange(newRange);
              setIsAllTime(false);
            }
          }}
          isDisabled={isAllTime}
          maxVisibleMonths={2}
          styles={style({ width: 260 })}
        />
        <Switch
          isSelected={isAllTime}
          onChange={(selected) => {
            setIsAllTime(selected);
            if (!selected && (!range?.start || !range?.end)) {
              setRange(getDefaultDateRange());
            }
          }}
        >
          All time
        </Switch>
         <Switch isSelected={excludeDevTeam} onChange={setExcludeDevTeam}>
          Exclude dev team
        </Switch>
        <Text styles={style({ font: "body-sm", alignSelf: "center" })}>
          {totalCount} total
        </Text>
      </div>

      <TableView
        aria-label="All projects"
        loadingState={loadingState}
        onLoadMore={handleLoadMore}
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        renderEmptyState={() =>
          isError
            ? (error?.message ?? "Failed to load projects")
            : "No projects found"
        }
        overflowMode="wrap"
        density="spacious"
        styles={style({ maxHeight: "full", height: "100%" })}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <Column
              isRowHeader={column.isRowHeader}
              allowsSorting={column.allowsSorting}
              width={column.width}
              minWidth={column.minWidth}
              align={column.align}
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        <TableBody>
          <Collection
            items={rows}
            dependencies={[
              templatesById,
              updateCommentMutation.isPending,
              updateCommentMutation.variables,
              duplicateVideo.isPending,
              duplicateVideo.variables,
              downloadingAssetsVideoId,
              pendingCurlJobId,
              jobRetry.isPending,
              jobRetry.variables,
              curlByJobId,
            ]}
          >
            {(item) => {
              const badge = STATUS_BADGE[item.status] ?? STATUS_BADGE.draft;
              const stepLabel = getProjectStepLabel(item, templatesById) || "—";
              const stepProgress = getProjectStepProgress(item, templatesById);
              const createdDate = item.createdAt
                ? new Date(item.createdAt)
                : null;
              const formattedDate =
                createdDate && !Number.isNaN(createdDate.getTime())
                  ? formatter.format(createdDate)
                  : "—";
              const lastAction = item.updatedAt
                ? getRelativeTime(item.updatedAt) || "—"
                : "—";
              const customerLabel = getCustomerOrAccountLabel(item) || "—";
              const jobs = Array.isArray(item.jobs) ? item.jobs : [];
              const history = getVideoHistory(item);

              return (
                <Row id={item.id}>
                  <Cell>
                    <ActionMenu
                      isQuiet
                      size="S"
                      aria-label={`Actions for ${item.name || item.id}`}
                      disabledKeys={[
                        ...(duplicateVideo.isPending &&
                        duplicateVideo.variables?.id === item.id
                          ? ["duplicate"]
                          : []),
                        ...(getExpressUrl(item) ? [] : ["express"]),
                        ...(downloadingAssetsVideoId ? ["assets"] : []),
                        ...(item.videoPath ? [] : ["video"]),
                      ]}
                      onAction={(key) => {
                        if (key === "duplicate") handleDuplicate(item);
                        if (key === "express") {
                          window.open(
                            getExpressUrl(item),
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }
                        if (key === "assets") {
                          setDownloadingAssetsVideoId(item.id);
                        }
                        if (key === "video") handleDownloadVideo(item);
                      }}
                    >
                      <MenuItem id="express">
                        <ExpressIcon />
                        <Text>Open in Express</Text>
                      </MenuItem>
                      <MenuItem id="assets">
                        <DownloadIcon />
                        <Text>
                          {downloadingAssetsVideoId === item.id
                            ? "Preparing assets…"
                            : "Download assets"}
                        </Text>
                      </MenuItem>
                      <MenuItem id="video">
                        <DownloadIcon />
                        <Text>Download video</Text>
                      </MenuItem>
                      <MenuItem id="duplicate">
                        <DuplicateIcon />
                        <Text>
                          {duplicateVideo.isPending &&
                          duplicateVideo.variables?.id === item.id
                            ? "Duplicating…"
                            : "Duplicate"}
                        </Text>
                      </MenuItem>
                    </ActionMenu>
                  </Cell>
                  <Cell>
                    <div
                      className={style({
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                        gap: 4,
                      })}
                    >
                      <Badge size="S" variant={badge.variant}>
                        {badge.label}
                      </Badge>
                      {item.duplicatedFrom?.id && (
                        <AriaLink
                          href={
                            videosById.has(item.duplicatedFrom.id)
                              ? getProjectHref(
                                  videosById.get(item.duplicatedFrom.id),
                                )
                              : getNewVideoPath(item.duplicatedFrom.id)
                          }
                          aria-label={`Copy of ${item.duplicatedFrom.name || item.duplicatedFrom.id}`}
                          className={copyBadgeLinkStyle}
                        >
                          <Badge size="S" variant="neutral">
                            Copy
                          </Badge>
                        </AriaLink>
                      )}
                    </div>
                  </Cell>
                  <Cell>
                    {templatesById.get(item.templateId)?.name ||
                      item.templateId ||
                      item.templateType ||
                      "—"}
                  </Cell>
                  <Cell>
                    {stepLabel}
                    {stepProgress && (
                      <span
                        className={style({
                          font: "detail-sm",
                          color: "gray-600",
                          marginStart: 4,
                          whiteSpace: "nowrap",
                        })}
                      >
                        {stepProgress.step}/{stepProgress.total}
                      </span>
                    )}
                  </Cell>
                  <Cell>
                    <Link href={getProjectHref(item)} isQuiet>
                      {item.name || item.id}
                    </Link>
                  </Cell>
                  <Cell>
                    {jobs.length === 0 && history.length === 0 ? (
                      "—"
                    ) : (
                      <DialogTrigger>
                        <ActionButton
                          size="S"
                          isQuiet
                          aria-label={`Show jobs and video history for ${item.name || item.id}`}
                        >
                          <ChevronDownIcon />
                          <Text>
                            {jobs.length > 0
                              ? `Jobs (${jobs.length})`
                              : `Videos (${history.length})`}
                          </Text>
                        </ActionButton>
                        <Popover placement="bottom start">
                          <div
                            className={style({
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                              width: 480,
                              maxHeight: 480,
                              overflowY: "auto",
                            })}
                          >
                            {history.length > 0 && (
                              <>
                                <span
                                  className={style({
                                    font: "detail",
                                    color: "neutral-subdued",
                                  })}
                                >
                                  Video history ({history.length})
                                </span>
                                {history.map((entry) => {
                                  const isCurrent =
                                    entry.videoPath === item.videoPath;
                                  const date =
                                    entry.completedAt ?? entry.archivedAt;
                                  return (
                                    <div
                                      key={entry.videoPath}
                                      className={style({
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 8,
                                        padding: 8,
                                        borderRadius: "default",
                                        backgroundColor: "layer-1",
                                      })}
                                    >
                                      <div
                                        className={style({
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 4,
                                          minWidth: 0,
                                        })}
                                      >
                                        <span
                                          className={style({ font: "body-xs" })}
                                        >
                                          {date
                                            ? formatter.format(new Date(date))
                                            : "Before history tracking"}
                                          {entry.generationTimeMs != null &&
                                            ` · took ${formatDuration(entry.generationTimeMs)}`}
                                        </span>
                                        <span
                                          title={entry.videoPath}
                                          className={style({
                                            font: "code-xs",
                                            color: "neutral-subdued",
                                            overflowWrap: "anywhere",
                                          })}
                                        >
                                          {entry.jobId
                                            ? `job ${entry.jobId}`
                                            : entry.videoPath}
                                          {entry.createdBy &&
                                            ` · ${entry.createdBy}`}
                                        </span>
                                      </div>
                                      <div
                                        className={style({
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 8,
                                          flexShrink: 0,
                                        })}
                                      >
                                        {isCurrent && (
                                          <Badge size="S" variant="positive">
                                            Current
                                          </Badge>
                                        )}
                                        <ActionButton
                                          size="XS"
                                          aria-label={`Download video ${entry.videoPath}`}
                                          onPress={() =>
                                            handleDownloadVideo(
                                              item,
                                              entry.videoPath,
                                            )
                                          }
                                        >
                                          <DownloadIcon />
                                        </ActionButton>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                            {jobs.length > 0 && (
                              <span
                                className={style({
                                  font: "detail",
                                  color: "neutral-subdued",
                                })}
                              >
                                Jobs ({jobs.length})
                              </span>
                            )}
                            {jobs.map((job) => {
                              const jobBadge = JOB_STATUS_BADGE[job.status] ?? {
                                variant: "neutral",
                                label: job.status || "Unknown",
                              };
                              const isGeneratingJobCurl =
                                pendingCurlJobId === job.jobId;
                              const generatedCurl = curlByJobId[job.jobId];
                              const isRetryingJob =
                                jobRetry.isPending &&
                                jobRetry.variables?.jobId === job.jobId;

                              return (
                                <div
                                  key={job.jobId}
                                  className={style({
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    padding: 8,
                                    borderRadius: "default",
                                    backgroundColor: "layer-1",
                                    width: "full",
                                    boxSizing: "border-box",
                                  })}
                                >
                                  {job.moduleName && (
                                    <span
                                      className={style({ font: "body-xs" })}
                                    >
                                      {job.moduleName}
                                    </span>
                                  )}
                                  <div
                                    className={style({
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    })}
                                  >
                                    <span
                                      className={style({
                                        font: "code-xs",
                                        color: "neutral-subdued",
                                        userSelect: "all",
                                        overflowWrap: "anywhere",
                                      })}
                                    >
                                      {job.jobId}
                                    </span>
                                    <ActionButton
                                      size="XS"
                                      isQuiet
                                      aria-label={`Copy job ID ${job.jobId}`}
                                      onPress={() => handleCopyJobId(job.jobId)}
                                    >
                                      <CopyIcon />
                                    </ActionButton>
                                  </div>
                                  <span
                                    className={style({
                                      font: "body-xs",
                                      color: "neutral-subdued",
                                    })}
                                  >
                                    {[
                                      job.createdAt
                                        ? `Created ${formatter.format(new Date(job.createdAt))}`
                                        : null,
                                      getJobDurationLabel(job),
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </span>
                                  {job.retryOf && (
                                    <span
                                      className={style({
                                        font: "body-xs",
                                        color: "neutral-subdued",
                                        overflowWrap: "anywhere",
                                      })}
                                    >
                                      Retry of {job.retryOf}
                                    </span>
                                  )}
                                  {job.status === "failed" &&
                                    getJobErrorMessage(job) && (
                                      <span
                                        className={style({
                                          font: "body-xs",
                                          color: "negative",
                                          overflowWrap: "anywhere",
                                        })}
                                      >
                                        {getJobErrorMessage(job)}
                                      </span>
                                    )}
                                  <div
                                    className={style({
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "end",
                                      gap: 8,
                                    })}
                                  >
                                    <div
                                      className={style({
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                      })}
                                    >
                                      <Badge
                                        size="S"
                                        variant={jobBadge.variant}
                                      >
                                        {jobBadge.label}
                                      </Badge>
                                      {canRetryUpload(job) && (
                                        <ActionButton
                                          size="XS"
                                          aria-label={`Retry upload for job ${job.jobId}`}
                                          isDisabled={isRetryingJob}
                                          onPress={() =>
                                            handleJobRetry(
                                              job.jobId,
                                              "retry-upload",
                                            )
                                          }
                                        >
                                          <RefreshIcon />
                                          <Text>Retry upload</Text>
                                        </ActionButton>
                                      )}
                                      {canRerender(job) && (
                                        <ActionButton
                                          size="XS"
                                          aria-label={`Re-render job ${job.jobId}`}
                                          isDisabled={isRetryingJob}
                                          onPress={() =>
                                            handleJobRetry(
                                              job.jobId,
                                              "rerender",
                                            )
                                          }
                                        >
                                          <RefreshIcon />
                                          <Text>Re-render</Text>
                                        </ActionButton>
                                      )}
                                      {job.hasRawRenderRequest && (
                                        <ActionButton
                                          size="XS"
                                          aria-label={`Produce cURL for job ${job.jobId}`}
                                          isDisabled={isGeneratingJobCurl}
                                          onPress={() =>
                                            handleProduceCurl(job.jobId)
                                          }
                                        >
                                          <CopyIcon />
                                          <Text>
                                            {isGeneratingJobCurl
                                              ? "Generating…"
                                              : "cURL"}
                                          </Text>
                                        </ActionButton>
                                      )}
                                    </div>
                                  </div>
                                  {generatedCurl && (
                                    <code
                                      className={style({
                                        font: "code-xs",
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                        maxHeight: 120,
                                        overflowY: "auto",
                                        display: "block",
                                      })}
                                    >
                                      {generatedCurl}
                                    </code>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </Popover>
                      </DialogTrigger>
                    )}
                  </Cell>
                  <Cell>{formattedDate}</Cell>
                  <Cell>{lastAction}</Cell>
                  <Cell>{customerLabel}</Cell>
                  <Cell>{item.createdBy || "—"}</Cell>
                  <Cell>
                    {getSharedWith(item).length > 0
                      ? getSharedWith(item).join(", ")
                      : "—"}
                  </Cell>
                  <Cell>
                    {typeof item.rating === "number" ? (
                      <span
                        className={style({
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        })}
                      >
                        <StarFilledIcon styles={style({ size: 14 })} />
                        <span>{item.rating}/5</span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </Cell>
                  <Cell>
                    <span
                      className={style({
                        font: "body-sm",
                        whiteSpace: "pre-wrap",
                      })}
                    >
                      {item.feedbackMessage || "—"}
                    </span>
                  </Cell>
                  <EditableCell
                    isSaving={isSavingComment(item)}
                    onSubmit={submitComment(item)}
                    renderEditing={() => (
                      <TextArea
                        autoFocus
                        aria-label="Comment"
                        name="comment"
                        defaultValue={item.comment ?? ""}
                        styles={style({ width: "full", minWidth: 360 })}
                      />
                    )}
                  >
                    <div
                      className={style({
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        justifyContent: "space-between",
                        width: "full",
                      })}
                    >
                      <p
                        className={style({
                          font: "body-sm",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        })}
                      >
                        {item.comment || "—"}
                      </p>
                      <ActionButton
                        slot="edit"
                        aria-label={`Edit comment for ${item.name || item.id}`}
                        size="S"
                      >
                        <EditIcon />
                      </ActionButton>
                    </div>
                  </EditableCell>
                </Row>
              );
            }}
          </Collection>
        </TableBody>
      </TableView>
    </div>
  );
};
